import { type JSX, useEffect, useId, useState } from 'react';
import { uiText } from '../i18n.js';
import { cn } from '../lib/utils.js';
import { CodeBlock } from './code-block.js';

/* Mermaid é pesado (d3/dagre) e sensível a CSP no Electron: entra SÓ via dynamic
   import (fora do bundle inicial) e renderiza client-side sob demanda. Qualquer
   falha — parse inválido, worker/CSP bloqueado, ou SSR (sem useEffect) — cai no
   CodeBlock com o código cru, nunca quebrando a doc (ADR-0722). securityLevel
   'strict' usa o DOMPurify interno do mermaid, tornando o SVG de saída seguro
   para `dangerouslySetInnerHTML`. */

/** Detecta tema efetivo para pintar o diagrama alinhado ao app (data-theme vence). */
function prefersDark(): boolean {
  if (typeof document !== 'undefined') {
    const theme = document.documentElement.getAttribute('data-theme');
    if (theme === 'dark') return true;
    if (theme === 'light') return false;
  }
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;
}

export function MermaidDiagram(props: {
  readonly code: string;
  readonly className?: string;
}): JSX.Element {
  const { code, className } = props;
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  /* `useId` traz ':' — inválido em querySelector/id de SVG que o mermaid usa. */
  const renderId = `mermaid-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  useEffect(() => {
    let active = true;
    import('mermaid')
      .then(async (mod) => {
        mod.default.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: prefersDark() ? 'dark' : 'default',
        });
        const result = await mod.default.render(renderId, code);
        if (active) setSvg(result.svg);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [code, renderId]);

  /* Carregando (ou SSR) e erro/CSP: código cru. Mermaid não é linguagem shiki →
     o CodeBlock cai no fallback plain, ainda com botão copiar. */
  if (failed || svg === null) {
    return <CodeBlock code={code} language="mermaid" className={className} />;
  }

  return (
    <div
      data-slot="mermaid"
      role="img"
      aria-label={uiText('markdown.mermaid.diagram_label')}
      className={cn(
        'my-3 flex justify-center overflow-x-auto rounded-md border border-border bg-card p-3',
        className,
      )}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG gerado pelo mermaid com securityLevel 'strict' (DOMPurify interno). A fonte do diagrama já passou pelo rehype-sanitize do markdown; o mermaid sanitiza o SVG de saída.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
